import React from 'react';
import type { OnboardingAnswers } from '../../../../shared/types/accounts';
import { STACK_OPTIONS } from '../../../../shared/lib/stackOptions';

interface Props {
  stacks: OnboardingAnswers['stacks'];
  onChange: (stacks: OnboardingAnswers['stacks']) => void;
}

// Onboarding only offers `flutter`/`unity`/`none` for mobile — settingsCopy's
// full STACK_OPTIONS mobile list adds `react-native`/`ios-swift` for the
// project-settings single-value picker (UX-06), which don't apply here.
const ONBOARDING_MOBILE_VALUES = new Set(['flutter', 'unity', 'none']);
const ONBOARDING_STACK_OPTIONS = STACK_OPTIONS.map((s) =>
  s.slot === 'mobile'
    ? { ...s, options: s.options.filter((o) => ONBOARDING_MOBILE_VALUES.has(o.value)) }
    : s,
) as Array<{ slot: keyof OnboardingAnswers['stacks']; label: string; options: Array<{ value: string; label: string }> }>;

function toggle(arr: string[], val: string): string[] {
  return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

/**
 * UX-08: body-only — the pill-toggle groups from the old `StepStack`
 * step, minus its own Back/Continue footer, so `StepCodebase` can compose
 * it with `ScaleFields` and `NeedsFields` on one screen.
 */
export const StackFields: React.FC<Props> = ({ stacks, onChange }) => (
  <div className="space-y-4">
    {ONBOARDING_STACK_OPTIONS.map(({ slot, label, options }) => (
      <div key={slot}>
        <p className="text-xs font-medium text-slate-700 mb-2">{label}</p>
        <div className="flex flex-wrap gap-2">
          {options.map(({ value, label: optLabel }) => {
            const selected = (stacks[slot] ?? []).includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => onChange({ ...stacks, [slot]: toggle(stacks[slot] ?? [], value) })}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selected
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
                }`}
              >
                {optLabel}
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);
